import { useState, useEffect, useMemo } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InvoicePDF } from "@/components/Billing/InvoicePDF";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft, Plus, Trash2, Save, Send, FileText, Calculator,
  Building2, Calendar, Euro, AlertCircle, Truck, Eye, Landmark
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { format, addDays } from "date-fns";
import { toast } from "sonner";
import type { Invoice, InvoiceLine, BillingCustomer, TaxRate, InvoiceSeries } from "@/types/billing";

interface LineForm {
  reference?: string;
  id?: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_percent: number;
  tax_rate: number;
}

const emptyLine: LineForm = {
  reference: '',
  description: '',
  quantity: 1,
  unit: 'UND',
  unit_price: 0,
  discount_percent: 0,
  tax_rate: 21,
};

export default function InvoiceEditor() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !id || id === 'new';

  // Form state
  const [invoice, setInvoice] = useState<Partial<Invoice>>({
    invoice_type: 'standard',
    issue_date: format(new Date(), 'yyyy-MM-dd'),
    status: 'draft',
    subtotal: 0,
    total_tax: 0,
    total_amount: 0,
    paid_amount: 0,
  });
  const [lines, setLines] = useState<LineForm[]>([{ ...emptyLine }]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [isSporadicCustomer, setIsSporadicCustomer] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saveCustomerAfter, setSaveCustomerAfter] = useState(false);

  // Fetch existing invoice
  const { data: existingInvoice, isLoading: loadingInvoice } = useQuery({
    queryKey: ["invoice-detail", id],
    queryFn: async () => {
      if (isNew) return null;
      const { data, error } = await api
        .from("invoices")
        .select(`
          *,
          customer:billing_customers(*),
          lines:invoice_lines(*)
        `)
        .eq("id", id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !isNew,
  });

  // Load existing invoice into form
  useEffect(() => {
    if (existingInvoice) {
      setInvoice(existingInvoice);
      setSelectedCustomerId(existingInvoice.customer_id || '');
      if (existingInvoice.lines?.length > 0) {
        setLines(existingInvoice.lines.map((l: InvoiceLine) => ({
          id: l.id,
          reference: l.reference,
          description: l.description,
          quantity: l.quantity,
          unit: l.unit,
          unit_price: l.unit_price,
          discount_percent: l.discount_percent,
          tax_rate: l.tax_rate,
        })));
      }
    }
  }, [existingInvoice]);

  // Fetch billing settings (para IBAN)
  const { data: billingSettings } = useQuery({
    queryKey: ["billing-settings"],
    queryFn: async () => {
      const { data } = await api.from("billing_settings").select("*").limit(1).single();
      return data;
    },
  });

  // Fetch customers
  const { data: customers = [] } = useQuery<BillingCustomer[]>({
    queryKey: ["billing-customers-select"],
    queryFn: async () => {
      const { data, error } = await api
        .from("billing_customers")
        .select("*")
        .eq("is_active", true)
        .order("legal_name");
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch tax rates
  const { data: taxRates = [] } = useQuery<TaxRate[]>({
    queryKey: ["tax-rates"],
    queryFn: async () => {
      const { data, error } = await api
        .from("tax_rates")
        .select("*")
        .eq("is_active", true)
        .order("rate", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch product_models filtered by selected customer (for reference selector)
  const { data: productModels = [] } = useQuery({
    queryKey: ["product-models-invoice", selectedCustomerId],
    queryFn: async () => {
      let query = api.from("product_models").select("id, reference, name, customer_id").eq("is_active", true).order("reference");
      if (selectedCustomerId) {
        query = query.eq("customer_id", selectedCustomerId);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch series
  const { data: series = [] } = useQuery<InvoiceSeries[]>({
    queryKey: ["invoice-series"],
    queryFn: async () => {
      const { data, error } = await api
        .from("invoice_series")
        .select("*")
        .eq("is_active", true)
        .order("reference");
      if (error) throw error;
      return data || [];
    },
  });

  // Calculate totals
  const totals = useMemo(() => {
    let subtotal = 0;
    let totalTax = 0;
    const taxBreakdown: Record<number, { base: number; tax: number }> = {};

    for (const line of lines) {
      if (!line.description || line.unit_price <= 0) continue;

      const lineSubtotal = line.quantity * line.unit_price * (1 - line.discount_percent / 100);
      const lineTax = lineSubtotal * line.tax_rate / 100;

      subtotal += lineSubtotal;
      totalTax += lineTax;

      if (!taxBreakdown[line.tax_rate]) {
        taxBreakdown[line.tax_rate] = { base: 0, tax: 0 };
      }
      taxBreakdown[line.tax_rate].base += lineSubtotal;
      taxBreakdown[line.tax_rate].tax += lineTax;
    }

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      totalTax: Math.round(totalTax * 100) / 100,
      total: Math.round((subtotal + totalTax) * 100) / 100,
      taxBreakdown: Object.entries(taxBreakdown).map(([rate, values]) => ({
        tax_rate: parseFloat(rate),
        base: Math.round(values.base * 100) / 100,
        tax_amount: Math.round(values.tax * 100) / 100,
      })),
    };
  }, [lines]);

  // Handle customer selection
  const handleCustomerChange = (customerId: string) => {
    setSelectedCustomerId(customerId);
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      const dueDate = addDays(new Date(invoice.issue_date || new Date()), customer.default_payment_terms_days || 30);
      setInvoice({
        ...invoice,
        customer_id: customer.id,
        customer_tax_id: customer.tax_id || '',
        customer_name: customer.legal_name,
        customer_address: [
          customer.address_line1,
          customer.postal_code && customer.city ? `${customer.postal_code} ${customer.city}` : null,
          customer.province,
        ].filter(Boolean).join('\n'),
        payment_method: customer.default_payment_method || 'transfer',
        payment_terms_days: customer.default_payment_terms_days || 30,
        due_date: format(dueDate, 'yyyy-MM-dd'),
      });

      // Update tax rate on lines if customer has default
      if (customer.default_tax_rate_id) {
        const rate = taxRates.find(r => r.id === customer.default_tax_rate_id);
        if (rate) {
          setLines(lines.map(l => ({ ...l, tax_rate: rate.rate })));
        }
      }
    }
  };

  // Line management
  const addLine = () => {
    setLines([...lines, { ...emptyLine }]);
  };

  const addTransportLine = () => {
    setLines([...lines, {
      ...emptyLine,
      description: 'Gastos de transporte',
      quantity: 1,
      unit: 'UND',
      tax_rate: 21,
    }]);
  };

  const updateLine = (index: number, field: keyof LineForm, value: any) => {
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setLines(newLines);
  };

  const removeLine = (index: number) => {
    if (lines.length === 1) return;
    setLines(lines.filter((_, i) => i !== index));
  };

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (params: { emit?: boolean }) => {
      const { emit } = params;

      // Validate
      if (!invoice.customer_id && !invoice.customer_name) {
        throw new Error("Selecciona un cliente");
      }
      if (!lines.some(l => l.description && l.unit_price > 0)) {
        throw new Error("Añade al menos una línea con descripción y precio");
      }

      // Prepare invoice data
      const invoiceData: any = {
        invoice_type: invoice.invoice_type || 'standard',
        customer_id: invoice.customer_id,
        customer_tax_id: invoice.customer_tax_id,
        customer_name: invoice.customer_name,
        customer_address: invoice.customer_address,
        issue_date: invoice.issue_date,
        operation_date: invoice.operation_date || null,
        due_date: invoice.due_date || null,
        payment_method: invoice.payment_method,
        payment_terms_days: invoice.payment_terms_days,
        subtotal: totals.subtotal,
        total_tax: totals.totalTax,
        total_amount: totals.total,
        tax_breakdown: totals.taxBreakdown,
        notes: invoice.notes,
        internal_notes: invoice.internal_notes,
        work_order_id: invoice.work_order_id || null,
        updated_at: new Date().toISOString(),
        updated_by: user?.id,
      };

      // If emitting, generate number and change status
      if (emit && invoice.status === 'draft') {
        invoiceData.status = 'issued';
        // Get next invoice number via RPC
        const seriesCode = invoice.invoice_type === 'rectificativa' ? 'R' :
                          invoice.invoice_type === 'proforma' ? 'P' : 'F';
        const { data: numResult, error: numError } = await api.rpc('generate_invoice_number', {
          p_series_code: seriesCode
        });
        if (numError) throw numError;
        invoiceData.invoice_number = numResult;
        invoiceData.series_id = series.find(s => s.code === seriesCode)?.id;
      }

      let invoiceId = invoice.id;

      if (isNew || !invoice.id) {
        // Insert new invoice
        invoiceData.created_by = user?.id;
        invoiceData.status = emit ? 'issued' : 'draft';
        const { data: inserted, error: insertError } = await api
          .from("invoices")
          .insert(invoiceData)
          .select("id")
          .single();
        if (insertError) throw insertError;
        invoiceId = inserted.id;
      } else {
        // Update existing
        const { error: updateError } = await api
          .from("invoices")
          .update(invoiceData)
          .eq("id", invoice.id);
        if (updateError) throw updateError;
      }

      // Delete existing lines and insert new ones
      if (!isNew && invoice.id) {
        await api.from("invoice_lines").delete().eq("invoice_id", invoice.id);
      }

      // Insert lines
      const lineData = lines
        .filter(l => l.description && l.unit_price > 0)
        .map((l, idx) => {
          const lineSubtotal = l.quantity * l.unit_price * (1 - l.discount_percent / 100);
          const lineTax = lineSubtotal * l.tax_rate / 100;
          return {
            invoice_id: invoiceId,
            line_number: idx + 1,
            reference: l.reference,
            description: l.description,
            quantity: l.quantity,
            unit: l.unit,
            unit_price: l.unit_price,
            discount_percent: l.discount_percent,
            discount_amount: Math.round(l.quantity * l.unit_price * l.discount_percent / 100 * 100) / 100,
            subtotal: Math.round(lineSubtotal * 100) / 100,
            tax_rate: l.tax_rate,
            tax_amount: Math.round(lineTax * 100) / 100,
            total: Math.round((lineSubtotal + lineTax) * 100) / 100,
          };
        });

      if (lineData.length > 0) {
        const { error: linesError } = await api.from("invoice_lines").insert(lineData);
        if (linesError) throw linesError;
      }

      // Si es cliente esporadico y quiere guardarlo, crear en billing_customers
      if (isSporadicCustomer && saveCustomerAfter && invoice.customer_name) {
        const { error: customerError } = await api.from("billing_customers").insert({
          legal_name: invoice.customer_name,
          tax_id: invoice.customer_tax_id || null,
          address_line1: invoice.customer_address || null,
          is_active: true,
        });
        if (!customerError) {
          toast.success("Cliente guardado para futuras facturas");
        }
      }

      // Crear asiento contable automatico al emitir
      if (emit && invoiceId) {
        await api.rpc("create_invoice_journal_entry", { p_invoice_id: invoiceId });
      }

      return invoiceId;
    },
    onSuccess: (invoiceId, params) => {
      queryClient.invalidateQueries({ queryKey: ["billing-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["invoice-detail", invoiceId] });
      toast.success(params.emit ? "Factura emitida correctamente" : "Factura guardada");
      navigate(`/billing/invoices/${invoiceId}`);
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (!isNew && loadingInvoice) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Cargando factura...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/billing/invoices"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold">
                {isNew ? "Nueva Factura" : `Factura ${invoice.invoice_number || 'Borrador'}`}
              </h1>
              <p className="text-muted-foreground">
                {invoice.status === 'draft' ? 'Borrador - sin numerar' : 'Emitida'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(true)}
            >
              <Eye className="w-4 h-4 mr-2" />
              Vista previa
            </Button>
            <Button
              variant="outline"
              onClick={() => saveMutation.mutate({ emit: false })}
              disabled={saveMutation.isPending}
            >
              <Save className="w-4 h-4 mr-2" />
              Guardar borrador
            </Button>
            {invoice.status === 'draft' && (
              <Button
                onClick={() => saveMutation.mutate({ emit: true })}
                disabled={saveMutation.isPending}
              >
                <Send className="w-4 h-4 mr-2" />
                Emitir factura
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Cliente
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <Label>Cliente esporadico (sin guardar)</Label>
                  <Switch
                    checked={isSporadicCustomer}
                    onCheckedChange={(checked) => {
                      setIsSporadicCustomer(checked);
                      if (checked) {
                        setSelectedCustomerId('');
                        setInvoice({ ...invoice, customer_id: null, customer_name: '', customer_tax_id: '', customer_address: '' });
                      }
                    }}
                  />
                </div>

                {!isSporadicCustomer ? (
                  <div className="space-y-2">
                    <Label>Seleccionar cliente</Label>
                    <SearchableSelect
                      options={customers.map((c) => ({
                        value: c.id,
                        label: c.legal_name,
                        description: c.tax_id || undefined
                      }))}
                      value={selectedCustomerId}
                      onValueChange={handleCustomerChange}
                      placeholder="Seleccionar cliente..."
                      searchPlaceholder="Buscar por nombre o NIF..."
                      allowClear={false}
                    />
                  </div>
                ) : (
                  <div className="space-y-4 p-4 border rounded-lg bg-amber-50 dark:bg-amber-950/20">
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      Introduce los datos del cliente manualmente
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nombre / Razon Social *</Label>
                        <Input
                          value={invoice.customer_name || ''}
                          onChange={(e) => setInvoice({ ...invoice, customer_name: e.target.value })}
                          placeholder="Nombre o empresa"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>NIF/CIF *</Label>
                        <Input
                          value={invoice.customer_tax_id || ''}
                          onChange={(e) => setInvoice({ ...invoice, customer_tax_id: e.target.value.toUpperCase() })}
                          placeholder="B12345678"
                          className="font-mono"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Direccion</Label>
                      <Input
                        value={invoice.customer_address || ''}
                        onChange={(e) => setInvoice({ ...invoice, customer_address: e.target.value })}
                        placeholder="Calle, numero, CP, Ciudad"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-2">
                      <Checkbox
                        id="save-customer"
                        checked={saveCustomerAfter}
                        onCheckedChange={(checked) => setSaveCustomerAfter(checked === true)}
                      />
                      <Label htmlFor="save-customer" className="text-sm cursor-pointer">
                        Guardar este cliente para futuras facturas
                      </Label>
                    </div>
                  </div>
                )}

                {!isSporadicCustomer && invoice.customer_name && (
                  <div className="p-4 bg-muted rounded-lg space-y-2">
                    <div className="flex justify-between">
                      <span className="font-medium">{invoice.customer_name}</span>
                      <span className="font-mono text-sm">{invoice.customer_tax_id}</span>
                    </div>
                    {invoice.customer_address && (
                      <p className="text-sm text-muted-foreground whitespace-pre-line">
                        {invoice.customer_address}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Invoice Lines */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Líneas de factura
                </CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" onClick={addLine}>
                    <Plus className="w-4 h-4 mr-1" />
                    Añadir línea
                  </Button>
                  <Button size="sm" variant="outline" onClick={addTransportLine}>
                    <Truck className="w-4 h-4 mr-1" />
                    + Transporte
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[15%]">Referencia</TableHead>
                      <TableHead className="w-[30%]">Descripción</TableHead>
                      <TableHead className="w-[12%]">Cantidad</TableHead>
                      <TableHead className="w-[14%]">Precio Ud.</TableHead>
                      <TableHead className="w-[10%]">Dto.%</TableHead>
                      <TableHead className="w-[10%]">IVA%</TableHead>
                      <TableHead className="w-[14%] text-right">Importe</TableHead>
                      <TableHead className="w-[5%]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.map((line, idx) => {
                      const lineTotal = line.quantity * line.unit_price * (1 - line.discount_percent / 100);
                      return (
                        <TableRow key={idx}>
                          <TableCell>
                            {productModels.length > 0 ? (
                              <SearchableSelect
                                value={line.reference || ""}
                                onValueChange={(val) => {
                                  updateLine(idx, "reference", val);
                                  const pm = productModels.find((p: any) => p.code === val);
                                  if (pm && !line.description) {
                                    updateLine(idx, "description", pm.name);
                                  }
                                }}
                                placeholder="Ref."
                                options={[
                                  { value: "", label: "Seleccionar..." },
                                  ...productModels.map((pm: any) => ({
                                    value: pm.reference,
                                    label: pm.reference + (pm.name ? " - " + pm.name : ""),
                                  })),
                                ]}
                              />
                            ) : (
                              <Input
                                value={line.reference || ""}
                                onChange={(e) => updateLine(idx, "reference", e.target.value)}
                                placeholder="Ref."
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Input
                              value={line.description}
                              onChange={(e) => updateLine(idx, 'description', e.target.value)}
                              placeholder="Descripción del producto/servicio"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={line.quantity}
                              onChange={(e) => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)}
                              min={0}
                              step={0.01}
                              className="text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={line.unit_price}
                              onChange={(e) => updateLine(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                              min={0}
                              step={0.01}
                              className="text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={line.discount_percent}
                              onChange={(e) => updateLine(idx, 'discount_percent', parseFloat(e.target.value) || 0)}
                              min={0}
                              max={100}
                              className="text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              value={line.tax_rate.toString()}
                              onValueChange={(v) => updateLine(idx, 'tax_rate', parseFloat(v))}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {taxRates.map((r) => (
                                  <SelectItem key={r.id} value={r.rate.toString()}>
                                    {r.rate}%
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(lineTotal)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeLine(idx)}
                              disabled={lines.length === 1}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle>Observaciones</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Notas (aparecen en la factura)</Label>
                  <Textarea
                    value={invoice.notes || ''}
                    onChange={(e) => setInvoice({ ...invoice, notes: e.target.value })}
                    rows={2}
                    placeholder="Condiciones de pago, observaciones para el cliente..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notas internas</Label>
                  <Textarea
                    value={invoice.internal_notes || ''}
                    onChange={(e) => setInvoice({ ...invoice, internal_notes: e.target.value })}
                    rows={2}
                    placeholder="Notas internas (no aparecen en la factura)"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Invoice Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Datos factura
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select
                    value={invoice.invoice_type}
                    onValueChange={(v) => setInvoice({ ...invoice, invoice_type: v as any })}
                    disabled={invoice.status !== 'draft'}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Factura</SelectItem>
                      <SelectItem value="rectificativa">Rectificativa</SelectItem>
                      <SelectItem value="proforma">Proforma</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Fecha emisión</Label>
                  <Input
                    type="date"
                    value={invoice.issue_date || ''}
                    onChange={(e) => setInvoice({ ...invoice, issue_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Fecha vencimiento</Label>
                  <Input
                    type="date"
                    value={invoice.due_date || ''}
                    onChange={(e) => setInvoice({ ...invoice, due_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Forma de pago</Label>
                  <Select
                    value={invoice.payment_method || 'transfer'}
                    onValueChange={(v) => setInvoice({ ...invoice, payment_method: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transfer">Transferencia</SelectItem>
                      <SelectItem value="cash">Efectivo</SelectItem>
                      <SelectItem value="card">Tarjeta</SelectItem>
                      <SelectItem value="direct_debit">Domiciliación</SelectItem>
                      <SelectItem value="check">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* IBAN para pagos */}
                {billingSettings?.bank_iban && (
                  <div className="space-y-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                    <Label className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
                      <Landmark className="w-4 h-4" />
                      Cuenta para pagos
                    </Label>
                    <div className="font-mono text-sm bg-white dark:bg-slate-900 p-2 rounded border">
                      {billingSettings.bank_iban.replace(/(.{4})/g, ' ').trim()}
                    </div>
                    {billingSettings.bank_name && (
                      <p className="text-xs text-muted-foreground">{billingSettings.bank_name}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Totals */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Totales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Base imponible</span>
                  <span className="font-mono">{formatCurrency(totals.subtotal)}</span>
                </div>

                {totals.taxBreakdown.map((tax, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">IVA {tax.tax_rate}%</span>
                    <span className="font-mono">{formatCurrency(tax.tax_amount)}</span>
                  </div>
                ))}

                <Separator />

                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="font-mono text-primary">{formatCurrency(totals.total)}</span>
                </div>
              </CardContent>
            </Card>

            {/* VeriFactu Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  VeriFactu
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  Al emitir la factura se generará el hash de cadena y quedará preparada para
                  envío a la AEAT cuando se configure la conexión VeriFactu.
                </p>
                {invoice.verifactu_hash && (
                  <div className="mt-2 p-2 bg-muted rounded text-xs font-mono break-all">
                    Hash: {invoice.verifactu_hash.substring(0, 16)}...
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Dialog Vista Previa */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Vista previa de factura</DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg p-4 bg-white">
            <InvoicePDF
              invoice={{
                ...invoice,
                lines: lines.filter(l => l.description && l.unit_price > 0).map((l, idx) => ({
                  line_number: idx + 1,
                  reference: l.reference,
                  description: l.description,
                  quantity: l.quantity,
                  unit: l.unit,
                  unit_price: l.unit_price,
                  discount_percent: l.discount_percent,
                  tax_rate: l.tax_rate,
                  subtotal: l.quantity * l.unit_price * (1 - l.discount_percent / 100),
                  tax_amount: l.quantity * l.unit_price * (1 - l.discount_percent / 100) * l.tax_rate / 100,
                  total: l.quantity * l.unit_price * (1 - l.discount_percent / 100) * (1 + l.tax_rate / 100),
                })),
                subtotal: totals.subtotal,
                total_tax: totals.totalTax,
                total_amount: totals.total,
                tax_breakdown: totals.taxBreakdown,
              }}
              settings={billingSettings || undefined}
            />
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
